import React from "react"
import { useTranslation } from "react-i18next"
import { ArrowDown } from "@phosphor-icons/react"
import { cn } from "@/lib/utils"
import { BodyMeasurementRow } from "@/modules/patients/detail/health-information/body-measurement/body-measurement-row"
import { getBodyMeasurementConnectionId } from "@/modules/patients/detail/health-information/body-measurement/lib/get-body-measurements-connection"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { PatientBodyMeasurementsOutputFragment$data } from "@/modules/patients/services/__generated__/PatientBodyMeasurementsOutputFragment.graphql"
import { useDeleteBodyMeasurement } from "@/modules/patients/services/useDeleteBodyMeasurement"
import { useConfirmStore } from "@/stores/useConfirmStore"

interface BodyMeasurementTableProps extends React.HTMLAttributes<HTMLDivElement> {
  bodyMeasurements: PatientBodyMeasurementsOutputFragment$data
  isEditMode?: boolean
  onEdit: (bodyMeasurement: PatientBodyMeasurementsOutputFragment$data[0]) => void
  editing: PatientBodyMeasurementsOutputFragment$data[0] | null
  showAsUnverified: boolean
  onVerify?: () => void
  onDelete?: () => void
}

const BodyMeasurementTable = ({
  bodyMeasurements,
  className,
  isEditMode = true,
  onEdit,
  editing,
  showAsUnverified,
  onVerify,
  onDelete,
}: BodyMeasurementTableProps) => {
  const { t } = useTranslation()
  const confirm = useConfirmStore((state) => state.confirm)
  const countryCode = useCountryParam()
  const patientId = usePatientParam()

  const deleteBodyMeasurement = useDeleteBodyMeasurement(countryCode)
  const connectionID = getBodyMeasurementConnectionId(patientId)

  const columns = [
    {
      header: (
        <span className="flex items-center gap-1 pl-1">
          {t("patient.healthInformation.bodyMeasurements.columnName.date")}
          <ArrowDown size={16} className="text-on-surface-subdued" />
        </span>
      ),
      id: "date",
    },
    {
      header: t("patient.healthInformation.bodyMeasurements.columnName.height"),
      id: "height",
    },
    {
      header: t("patient.healthInformation.bodyMeasurements.columnName.weight"),
      id: "weight",
    },
    {
      header: (
        <span className="flex justify-center">{t("patient.healthInformation.bodyMeasurements.columnName.bmi")}</span>
      ),
      id: "bodyMassIndex",
    },
    {
      id: "action",
      header: "",
    },
  ]
  if (!isEditMode) {
    columns.pop()
  }

  const handleDelete = async (id: string) => {
    const isConfirmed = await confirm({
      title: t("patient.healthInformation.bodyMeasurements.deleteConfirmTitle"),
      content: t("episodeOfCare.deleteProgressNote.content"),
      confirmVariant: "danger",
      confirmText: t("common.button.delete"),
      cancelText: t("common.button.cancel"),
    })

    if (isConfirmed) {
      deleteBodyMeasurement({ id }, connectionID)
      onDelete?.()
      onVerify?.()
    }
  }

  return (
    <div className={cn("text-on-surface grid w-full", className)} data-testid="body-measurement-list">
      <div
        data-testid="table-header-container"
        className={cn(
          "border-border text-on-surface-subdued bg-surface grid grid-cols-[90px_minmax(0,2fr)_minmax(0,2fr)_minmax(0,1fr)] gap-4 rounded-t-xl border px-4 py-2 text-xs",
          isEditMode && "grid-cols-[90px_minmax(0,2fr)_minmax(0,2fr)_minmax(0,1fr)_40px]",
        )}>
        {columns.map((header, idx) => {
          return (
            <div
              key={header.id}
              data-testid={`table-header-${header.id}`}
              className={cn("leading-4 tracking-[0.25px]", idx > 0 && "text-center")}>
              {header?.header}
            </div>
          )
        })}
      </div>
      <div
        data-testid="table-row-container"
        className="border-border bg-surface col-span-full grid divide-y overflow-hidden rounded-b-xl border-x border-b text-sm">
        {bodyMeasurements.map((bodyMeasurement, index) => {
          return (
            <BodyMeasurementRow
              isEditMode={isEditMode}
              key={bodyMeasurement.node.id}
              bodyMeasurement={bodyMeasurement.node}
              isEditing={editing ? bodyMeasurement.node.id === editing.node.id : false}
              onEdit={() => onEdit(bodyMeasurement)}
              onDelete={() => handleDelete(bodyMeasurement.node.id)}
              onVerify={index === 0 && showAsUnverified ? onVerify : undefined}
            />
          )
        })}
      </div>
    </div>
  )
}

export { BodyMeasurementTable }
